import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { DOMAIN, REASON, reportWarning, trace } from '~/services/telemetry';
import { bindCardStore, checkoutStore, mainStore, userStore } from '~/stores';
import { searchStore } from '~/stores/SearchStore';
import { checkAndSaveReferralPromocode } from '~/utils/referralLink';

import { storiesStore } from '../stores/StoriesStore';

export const useRN = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!mainStore.isRN) {
      return;
    }
    /**
     * Records the native handshake, and reports only when it is broken.
     *
     * Every successful `getDeviceInfo` used to be its own event: 42,827 of
     * them in six days, half of this project's entire ingest, describing
     * nothing but the app starting normally. The successful case is a
     * breadcrumb, so it still shows up inside whatever does fail afterwards;
     * an event is now what a handshake that cannot be used produces, which is
     * the thing the reporting existed to catch.
     */
    const trackHandshake = (msg: Record<string, any>) => {
      if (msg.targetFunc !== 'getDeviceInfo') {
        return;
      }

      /**
       * The bridge nests the payload under `args`; the envelope around it only
       * carries routing fields. Reading the envelope made every healthy
       * handshake look broken, so this reported once per session for every
       * single app user while `initRN` — which reads `args` — was working fine.
       */
      const payload = msg.args ?? {};

      trace(DOMAIN.rn, 'Native handshake received', {
        hasPlatform: Boolean(payload.platform),
        hasDeviceInfo: Boolean(payload.deviceInfo),
        nativeAppVersion: payload.nativeAppVersion,
      });

      if (!payload.platform || !payload.deviceInfo) {
        reportWarning(DOMAIN.rn, 'Native handshake payload is unusable', {
          reason: REASON.rn.handshakeUnusable,
          once: true,
          extras: { response: msg },
        });
      }
    };
    const handlerEvent = async (e: Event) => {
      if ((e as MessageEvent).origin === 'https://js.stripe.com') {
        return;
      }
      if ((e as MessageEvent).origin === 'https://jiffygrocery.co.uk') {
        return;
      }
      if (!(e as MessageEvent).data) {
        return;
      }
      if ((e as MessageEvent).data[0] !== '{') {
        return;
      }
      try {
        const message: Record<string, any> = JSON.parse(
          (e as MessageEvent).data || '{}',
        );
        trackHandshake(message);
        if (message.event === 'showed') {
          mainStore.setIsRNReady(true);
        }
        if (message.event === 'appStateBackground') {
          trace(DOMAIN.rn, 'App went to background');
          mainStore.setInBackground(true);
        }
        if (message.event === 'appStateActive') {
          trace(DOMAIN.rn, 'App became active');
          mainStore.setInBackground(false);
        }
        if (message.event === 'inAppBrowserClosed') {
          mainStore.setInAppBrowserClosedAt(Date.now());
          bindCardStore.handleInAppBrowserClosed();
        }
        if (message.event === 'octoPaymentReturn') {
          const { orderId, status } = (message.data || {}) as {
            orderId?: string;
            status?: string;
          };
          if (bindCardStore.step === 'webview') {
            if (status === 'canceled' || status === 'failed') {
              bindCardStore.handleHostedOtpFail();
            } else {
              bindCardStore.handleHostedOtpComplete();
            }
            return;
          }
          checkoutStore.completeOctoPayment(orderId, status, navigate);
        }
        if (message.event === 'AppStateBackground') {
          mainStore.clearAllCache();
        }
        if (message.event === 'setSafeAreaInsets') {
          const { top, bottom } = message.data || {};
          const topPx = Number(top);
          const bottomPx = Number(bottom);
          if (Number.isFinite(topPx)) {
            document.documentElement.style.setProperty(
              '--safe-area-inset-top-fallback',
              `${topPx}px`,
            );
          }
          if (Number.isFinite(bottomPx)) {
            document.documentElement.style.setProperty(
              '--safe-area-inset-bottom-fallback',
              `${bottomPx}px`,
            );
          }
        }
        if (message.event === 'deeplinkGetted') {
          if (
            message.universalLinkData?.key &&
            message.universalLinkData?.value
          ) {
            const url = new URL(window.location.href);

            url.searchParams.set(
              message.universalLinkData?.key,
              message.universalLinkData?.value,
            );

            window.history.pushState({ path: url.href }, '', url.href);

            await checkAndSaveReferralPromocode();
          }

          if (message.data?.deep_link_value) {
            const [type, value] = (
              message.data.deep_link_value as string
            ).split('_');
            let pathname = '';
            let state: Record<string, string | number> = {};
            if (type === 'product') {
              pathname = `/p/${value}`;
            }
            if (type === 'category') {
              pathname = `/c/${value}`;
            }
            if (type === 'promo') {
              pathname = `/banner/${value}`;
            }
            if (type === 'cart') {
              pathname = '/cart';
            }
            if (type === 'favourites') {
              pathname = '/favorites';
              if (userStore.deliveryAddress) {
                navigate(pathname, {
                  state: { isDeeplink: true, ...state },
                });
              } else {
                mainStore.setDeferedDeeplink(pathname);
              }
              return;
            }
            if (type === 'purchased') {
              pathname = '/favorites';
              state = { activeTabNum: 1 };
              if (userStore.deliveryAddress) {
                navigate(pathname, {
                  state: { isDeeplink: true, ...state },
                });
              } else {
                mainStore.setDeferedDeeplink(pathname);
              }
              return;
            }
            if (type === 'search') {
              pathname = '/search';
              searchStore.paginationRequest.loadPage(
                1,
                // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
                value.search(/%\D|%$/g) === -1
                  ? // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
                    decodeURIComponent(value)
                  : value,
              );
            }
            if (type === 'order-rate' && userStore.isAuthorized) {
              pathname = '/';
              mainStore.setDeferedRateOrder(true);
            }
            if (type === 'story') {
              storiesStore
                .requestStories()
                .catch((error) => error && console.error(error));
              const storyByDeepLink = storiesStore.stories.find(
                (story) => story.deepLink === message.data.deep_link_value,
              );
              if (storyByDeepLink) {
                pathname = `/stories/${storyByDeepLink.id}`;
              }
            }
            if (pathname) {
              navigate(pathname, {
                state: { isDeeplink: true, ...state },
              });
            }
          }
          if (message.navigationLink) {
            try {
              const url = new URL(message.navigationLink);
              const pathToNavigate = url.pathname + url.search + url.hash;

              if (message.navigationLink.includes('seus')) {
                return;
              }

              if (pathToNavigate && pathToNavigate !== '/') {
                navigate(pathToNavigate);
              }
            } catch {
              if (message.navigationLink && message.navigationLink !== '/') {
                navigate(message.navigationLink);
              }
            }
          }
        }
        if (mainStore.rnBridgeCallbacks[message.msgId]) {
          const callback = mainStore.rnBridgeCallbacks[message.msgId];
          if (message.isSuccessfull) {
            // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
            if (callback.onSuccess) {
              // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
              callback.onSuccess(message.args || {});
            }
          } else {
            // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
            if (callback.onError) {
              // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
              callback.onError(message.args || {});
            }
          }
          delete mainStore.rnBridgeCallbacks[message.msgId];
        }
      } catch (error) {
        error && console.error(error);
      }
    };
    document.addEventListener('message', handlerEvent);
    window.addEventListener('message', handlerEvent);
    mainStore.sendToRN('mount', {
      customerId: userStore.personalData.id || undefined,
    });
  }, []);

  useEffect(() => {
    if (!mainStore.isRN || !mainStore.isRNReady) {
      return;
    }
    mainStore.sendToRN('getDeviceInfo', null, (data) => mainStore.initRN(data));
  }, [mainStore.isRNReady]);
};
